import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Form, Button, Card, CardBody, CardTitle } from 'reactstrap';
import { Field, reduxForm, change } from 'redux-form';
import {
    DateTimePicker,
    SelectField,
    CashregistersField,
    PaymentMethodsField,
    AccountingTypesField
} from '../../../components';
import {
    loadTags,
    loadCashregisters,
    loadSalepoints,
    loadTktUsers,
    loadTransactionTypes,
    loadAccountingStats
} from '../../../redux';
import { AccountingStats, Utils } from '../../../services';
import { AccountingLineChart, AccountingBarChart, AccountingPieChart } from "../../../components/AccountingStats";
import moment from 'moment';

const StatsAccounting = ({ handleSubmit, submitting }) => {
    const { t, i18n } = useTranslation();
    const dispatch    = useDispatch();

    const money = amount => Utils.formatMoney(amount, i18n.language);

    const [filters, setFilters] = useState({
        from: moment().subtract(1, 'month').startOf('day').toISOString(),
        to: moment().endOf('day').toISOString(),
    });

    const { tags }          = useSelector(state => state.tags);
    const { tktusers }      = useSelector(state => state.tktusers);
    const { cashregisters } = useSelector(state => state.cashregisters);
    const { salepoints }    = useSelector(state => state.salepoints);
    const { isLoading }     = useSelector(state => state.accountingStats);

    const tagsOptions = React.useMemo(() => {
        return tags?.sort(
            (a, b) => a.name.localeCompare(b.name)
        ).map(tag => ({
            key: tag._id,
            text: tag.name,
            value: tag._id
        }));
    }, [tktusers]);

    const authorsOptions = React.useMemo(() => {
        const sellers = [
            Utils.TKTUSER_ROLE_ADMIN,
            Utils.TKTUSER_ROLE_POS,
            Utils.TKTUSER_ROLE_ON_SITE,
            Utils.TKTUSER_ROLE_TRANSFER,
            Utils.TKTUSER_ROLE_TICKET_EDITOR,
            Utils.TKTUSER_ROLE_REFUND,
            Utils.TKTUSER_ROLE_WALLET_CASHIER,
            Utils.TKTUSER_ROLE_WALLET_MANAGER,
            Utils.TKTUSER_ROLE_ESHOP
        ];
        return tktusers?.filter(
            user => user.roles.filter(
                r => sellers.includes(r)
            )?.length > 0
        ).sort(
            (a, b) => a.name.localeCompare(b.name)
        ).map(author => ({
            key: author._id,
            text: author.name,
            value: author.name
        }));
    }, [tktusers]);

    useEffect(() => {
        dispatch(loadTags());
        dispatch(loadTktUsers());
        dispatch(loadCashregisters());
        dispatch(loadSalepoints());
    }, []);

    const onSubmit = values => {
        setFilters({
            ...values,
            from: values.from.toISOString(),
            to: values.to.toISOString(),
            payment_methods: (values.payment_methods || []).join(','),
            tags: (values.tags || []).join(','),
            types: (values.types || []).join(','),
            cashregisters: (values.cashregisters || []).join(','),
            authors: (values.authors || []).join(','),
            groupBy: (values.groupBy || []).join(',')
        });
    };

    const filterOnChartValue = (property, value) => {
        if (filters[property]?.includes(value)) {
            dispatch(change('statsAccountingForm', property, []));
            setFilters({ ...filters, [property]: []});
        } else {
            dispatch(change('statsAccountingForm', property, [value]));
            setFilters({ ...filters, [property]: value});
        }
    }

    const cashregisterName = _id => {
        const cashregister = cashregisters?.find(c => c._id === _id);
        if (!cashregister)
            return null;

        const salepoint = salepoints?.find(s => s._id === cashregister.salepoint_id);
        if (!salepoint)
            return null;

        return `${Utils.localized_or_fallback(salepoint.name, i18n.language)} - ${Utils.localized_or_fallback(cashregister.name, i18n.language)}`;
    };

    return (
        <div className="StatsAccounting">
            <Row>
                <Col>
                    <Card>
                        <CardBody>
                            <CardTitle><i className="fa fa-filter"></i> {t('accounting.filters.title')}</CardTitle>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <Row>
                                    <Col xs={12} sm={6}>
                                        <label>{t('accounting.filters.start_date')}</label>
                                        <Field component={DateTimePicker} name="from" className="form-control"/>
                                    </Col>

                                    <Col xs={12} sm={6}>
                                        <label>{t('accounting.filters.end_date')}</label>
                                        <Field component={DateTimePicker} name="to" className="form-control"/>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} sm={6}>
                                        <label>{t('accounting.filters.transaction_type')}</label>
                                        <AccountingTypesField
                                            name="types"
                                            placeholder={t('accounting.filters.all_transaction_types')}
                                            multiple
                                        />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <label>{t('accounting.filters.payment_methods')}</label>
                                        <PaymentMethodsField
                                            name="payment_methods"
                                            placeholder={t('accounting.filters.payment_method_placeholder')}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} sm={6}>
                                        <label>{t('accounting.filters.tags')}</label>
                                        <SelectField
                                            options={tagsOptions}
                                            name="tags"
                                            placeholder={t('accounting.filters.all_tags')}
                                            multiple
                                        />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <label>{t('accounting.filters.cash_registers')}</label>
                                        <CashregistersField
                                            cashregisters={cashregisters}
                                            name="cashregisters"
                                            placeholder={t('accounting.filters.all_cash_registers')}
                                            multiple
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} sm={6}>
                                        <label>{t('accounting.filters.authors')}</label>
                                        <SelectField
                                            options={authorsOptions}
                                            name="authors"
                                            placeholder={t('accounting.filters.all_authors')}
                                            multiple
                                        />
                                    </Col>
                                    <Col xs={12} className="text-right mt-3">
                                        <Button type="submit" color="success" disabled={submitting || isLoading}>
                                            {isLoading ? (<span><i className="fa fa-spinner fa-spin"></i></span>) : (
                                                <span>{t('accounting.filters.show_results')} <i className="fa fa-arrow-circle-right"></i></span>)}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <AccountingBarChart
                        icon="fa-credit-card"
                        filters={ filters }
                        currency={Utils.currency()}
                        colorScheme="pastel1"
                        cashregisters={cashregisters}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={12} lg={6}>
                    <AccountingPieChart
                        group_by="payment_method"
                        icon="fa-credit-card"
                        filters={ filters }
                        currency={Utils.currency()}
                        colorScheme="pastel2"
                        chartDataBuilder={ result => ({
                            _id: result.payment_method,
                            id: t(`payment_methods.${result.payment_method}`),
                            label: t(`payment_methods.${result.payment_method}`),
                            value: result.total_amount,
                            data: { count: parseInt(result.count, 10) }
                        })}
                        onClickOnNode={id => filterOnChartValue('payment_methods', id)}
                    />
                </Col>
                <Col xs={12} lg={6}>
                    <AccountingPieChart
                        group_by="author"
                        icon="fa-user"
                        filters={ filters }
                        currency={Utils.currency()}
                        colorScheme="pastel1"
                        chartDataBuilder={ result => ({
                            _id: result.author,
                            id: result.author,
                            label: result.author,
                            value: result.total_amount,
                            data: { count: parseInt(result.count, 10) }
                        })}
                        onClickOnNode={id => filterOnChartValue('authors', id)}
                    />
                </Col>
            </Row>
            <Row>
                {(tagsOptions?.length > 0) && (
                    <Col xs={12}>
                        <AccountingPieChart
                            group_by="tag"
                            icon="fa-ticket"
                            filters={ filters }
                            currency={Utils.currency()}
                            colorScheme="nivo"
                            chartDataBuilder={ result => ({
                                _id: result.tag._id,
                                id: result.tag.name,
                                label: result.tag.name,
                                value: result.total_amount,
                                data: { count: parseInt(result.count, 10) }
                            })}
                            onClickOnNode={id => filterOnChartValue('tags', id)}
                        />
                    </Col>
                )}
            </Row>
            <Row>
                {(cashregisters?.length > 0) && (
                    <Col xs={12} lg={6}>
                        <AccountingPieChart
                            group_by="cashregister"
                            icon="fa-th"
                            filters={ filters }
                            currency={Utils.currency()}
                            colorScheme="pastel1"
                            chartDataBuilder={ result => ({
                                _id: result.cashregister,
                                id: cashregisterName(result.cashregister),
                                label: cashregisterName(result.cashregister),
                                value: result.total_amount,
                                data: { count: parseInt(result.count, 10) }
                            })}
                            onClickOnNode={id => filterOnChartValue('cashregisters', id)}
                        />
                    </Col>
                )}
                <Col xs={12} lg={6}>
                    <AccountingPieChart
                        group_by="type"
                        icon="fa-list-ul"
                        filters={ filters }
                        currency={Utils.currency()}
                        colorScheme="pastel1"
                        chartDataBuilder={ result => ({
                            _id: result.type,
                            id: t(`accounting.types.${result.type}`),
                            label: t(`accounting.types.${result.type}`),
                            value: result.total_amount,
                            data: { count: parseInt(result.count, 10) }
                        })}
                        onClickOnNode={id => filterOnChartValue('types', id)}
                    />
                </Col>
            </Row>
        </div>
    );
};

const StatsAccountingForm = reduxForm({
    form: 'statsAccountingForm',
    enableReinitialize: true,
    initialValues: {
        from: moment().subtract(1, 'month').startOf('day'),
        to: moment().endOf('day'),
    }
})(StatsAccounting);

export default StatsAccountingForm;
